import moment from "moment/moment";
import React from "react";
import { Button } from "react-bootstrap";
import { useParameterizedQuery } from "react-fetching-library";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import { getNewsData, NewsData } from "../../Actions/NewsAction";
import { AnimateBox } from "../../Components/GenericComponents/AnimateBox";
import { useVersionState } from "../../Contexts/ApiVersionProvider";
import { setNewsSectionData, useNewsSectionDispatch, useNewsSectionState } from "../../Contexts/NewsSectionProvider";
import { BlogSkeleton } from "./BlogSkeleton";
import { NewsCard } from "./NewsCard";
import { NewsModal } from "./NewsModal";
import {FaRegCheckSquare} from "react-icons/fa";

const falseNews : NewsData = {
    id: 0,
    title: 'Noticia no encontrada',
    description: 'La noticia que buscas no ha sido encontrada',
    content: '<h4>La noticia que buscas no ha sido encontrada</h4><p>Esto es realmente embarazoso. La noticia que usted intenta acceder no está disponible en este momento. Quizá lo estuvo en el pasado y ha sido borrado o quizá has escrito la dirección web mal. Si recibes este error, comprueba que la dirección que has introducido es correcta y no le falta o sobra nada.</p>',
    newsDate: moment().format('YYYY-MM-DDT00:00:00Z'),
    fuente: 'No Encontrado',
    link: '/',
    author: {
        id: 0,
        name: 'No Encontrado',
        image: '/page_react/images/person2.jpg'
    },
    is_highlight:false
};

export const AllNews: React.FunctionComponent = () => {
    const [isLoadData, setIsLoadData] = React.useState(false);

    const [newsSelected, setNewsSelected] = React.useState<NewsData>(falseNews);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    
    const [ishighlight, setIshighlight] = React.useState(false);
    const [isLoadingData, setIsLoadingData] = React.useState(false);

    const getNewaDataAction = useParameterizedQuery(getNewsData);
    const { data } = useVersionState()

    const version = data?.versions?.news;

    const newsSectionState = useNewsSectionState()
    const newsSectionDispatch = useNewsSectionDispatch()

    const newsSection = newsSectionState.data;

    const [filterStr, setFilterStr] = React.useState<string>('');
    const [filterDate, setFilterDate] = React.useState<string>('');

    const filteredNews = React.useMemo(() => {
    if (!newsSection) return [];
    
        const filterLower = filterStr.toLowerCase();
    
        let filterNews = newsSection.news
            .filter(news => {
                // Pasa si coincide el texto
                const matchText = news.title.toLowerCase().includes(filterLower) || 
                                (news.content && news.content.toLowerCase().includes(filterLower));
                
                // Pasa si vemos todas, o si vemos destacadas y la noticia es destacada
                const matchHighlight = !ishighlight || news.is_highlight;
                
                return matchText && matchHighlight;
            })

        if ( filterDate !== '' ){
            console.log(filterDate)
            filterNews = filterNews.filter( n => {
                return moment(n.newsDate).format('YYYY-MM-DD') === filterDate;
            } )
        }
            return filterNews.sort((a, b) => new Date(b.newsDate).getTime() - new Date(a.newsDate).getTime());
    }, [filterDate, filterStr, ishighlight, newsSection]);


    React.useEffect(() => {
        setTimeout( ()=>{
            const bannerTitle = document.getElementById('banner-title');
            const bannerSubtitle = document.getElementById('banner-subtitle');
            const bannerDescription = document.getElementById('banner-description');


            if( newsSection !== undefined ){
                if (bannerTitle) bannerTitle.innerHTML = newsSection.title;
                if (bannerSubtitle) bannerSubtitle.innerText = newsSection.subtitle
                if (bannerDescription) bannerDescription.innerText = newsSection.description;
            }
        }, 300 );
    }, [newsSection]);

    const forceDownload = React.useMemo( () => {
        if (newsSection?.version === undefined || version === undefined) {
            setIsLoadData(false);
            return true
        }

        if (newsSection.version !== version) {
            setIsLoadData(false);
            return true
        }

        setIsLoadData(true);
        return false
    }, [newsSection?.version, version] );

    React.useEffect(() => {
        if ( (forceDownload && version) || (version && version.length > 0 && !newsSection && !isLoadData) ){
            setIsLoadData(true);

            if ( getNewaDataAction.loading ) return;

            getNewaDataAction.query(version).then(response => {
                if (response.payload) {
                    setNewsSectionData(response.payload, newsSectionDispatch).then();
                }
            });
        }
    }, [forceDownload, data, getNewaDataAction, isLoadData, newsSection, newsSectionDispatch, version]);

    const { id, fechaStr } = useParams<{ id: string, fechaStr: string }>();
    const location = useLocation();

    const navigate = useNavigate();

    const highlightAction = React.useCallback(()=>{
        setIsLoadingData(true);
        setTimeout(()=>{ setIsLoadingData(false) }, 750)
        setTimeout(()=>{ setIshighlight(prev => !prev); }, 750)
    }, []);


    React.useEffect(()=>{
        if( location.pathname.indexOf("/noticias/ver-todas/por-fecha") >= 0 ){
            const currentDateSTR = moment().format('YYYY-MM-DD');
            if (fechaStr === undefined){
                navigate("/noticias/ver-todas/por-fecha/" + currentDateSTR );
            }else{
                setFilterDate(fechaStr);
            }
        }else{
            setFilterDate('');
        }
    }, [fechaStr, location.pathname, navigate])

    React.useEffect( ()=>{
        setIsModalOpen(false);
        if( newsSection !== undefined && id !== undefined ){
            const news = newsSection.news.find( news => news.id+'' === id );
            if( news !== undefined ){
                setNewsSelected(news);
            }else{
                setNewsSelected(falseNews);
            }

            setIsModalOpen(true);
        }
    }, [id, navigate, newsSection] );

    const [visibleCount, setVisibleCount] = React.useState(20);
    
    React.useEffect(() => {
        setVisibleCount(20);
    }, [filterStr, ishighlight]);

    const visibleNews = React.useMemo(() => {
        return filteredNews.slice(0, visibleCount);
    }, [filteredNews, visibleCount]);

    const observer = React.useRef<IntersectionObserver | null>(null);

    const sentinelRef = React.useCallback((node: HTMLDivElement | null) => {
        // Si ya estábamos observando algo, lo desconectamos
        if (observer.current) observer.current.disconnect();
        
        // Creamos un nuevo observador
        observer.current = new IntersectionObserver(entries => {
            // Si el elemento es visible en pantalla y aún hay noticias por cargar...
            if (entries[0].isIntersecting && visibleCount < filteredNews.length) {
                setVisibleCount(prev => prev + 20);
            }
        });
        
        // Le decimos que observe nuestro nodo
        if (node) observer.current.observe(node);
    }, [visibleCount, filteredNews.length]);


    if (newsSection === undefined || getNewaDataAction.loading) {
        return <BlogSkeleton />;
    }
    

    return (
        <React.Fragment>
            <NewsModal isOpen={ isModalOpen } news={ newsSelected } />
            <div id="fh5co-blog" className="fh5co-bg-section">
                <div className="container">
                    <AnimateBox className="row animate-box row-pb-md" data-animate-effect="fadeInUp">
                        <div className="col-sm-10 col-sm-offset-1 text-left fh5co-heading">
                            <form action="#">
                                <div className="row form-group">
                                    <div className="col-sm-6">
                                        <label htmlFor="fname">Búscas algo en especial...</label>
                                        <input type="text"
                                               id="fname"
                                               className="form-control"
                                               placeholder="Búsqueda de Noticias"
                                               onChange={ (e) => setFilterStr(e.target.value) }
                                               value={filterStr}
                                        />
                                    </div>

                                    <div className="col-sm-6">
                                        <label htmlFor="fdate">Puedes seleccionar una fecha</label>
                                        <input type="date"
                                               id="fdate"
                                               className="form-control"
                                               onChange={ (e) => {
                                                   const val = e.currentTarget.value
                                                   if (val === ''){
                                                       navigate('/noticias/ver-todas')
                                                   }else{
                                                       setIshighlight(false)
                                                       const currentDateSTR = moment().format('YYYY-MM-DD');
                                                       if( currentDateSTR === val ){
                                                           navigate('/noticias/ver-todas/por-fecha')
                                                       }else{
                                                           navigate('/noticias/ver-todas/por-fecha/' + val)
                                                       }
                                                   }
                                                   //setFilterDate(e.target.value)
                                               } }
                                               value={filterDate}
                                        />
                                    </div>
                                </div>
                                <div className="row form-group">
                                    <div className="col-sm-12" style={{textAlign:'right'}}>
                                        { filterDate === '' &&
                                            <Button
                                                disabled={isLoadingData}
                                                onClick={highlightAction}
                                            >
                                                {isLoadingData ? 'Procesando...' :
                                                    !ishighlight? 'Ver Solo Noticias Destacadas' : 'Dejar de Ver Solo Noticias Destacadas'
                                                }
                                            </Button>
                                        }
                                        {'  '}
                                        <Button
                                            onClick={ () => {
                                                if( filterDate === '' ){
                                                    setIshighlight(false)
                                                    navigate('/noticias/ver-todas/por-fecha')
                                                }else{
                                                    navigate('/noticias/ver-todas')
                                                }
                                            } }
                                        >
                                            { filterDate === '' ? 'Notas de Hoy' : 'Ver Todas' }
                                        </Button>
                                    </div>
                                </div>
                                
                            </form>
                        </div>
                    </AnimateBox>
                    {
                        filteredNews.length > 0 ?
                            (
                                <React.Fragment>
                                    <div className="row flex-row">
                                        {visibleNews.map(news => (
                                            <NewsCard key={`news-${news.id}`} news={news} isHome={false} />
                                        ))}
                                    </div>

                                    {visibleCount < filteredNews.length && (
                                        <div ref={sentinelRef} style={{ height: '20px', width: '100%', textAlign: 'center', margin: '20px 0' }}>
                                            <p className="text-muted">Cargando más noticias...</p>
                                        </div>
                                    )}
                                </React.Fragment>
                            ) :
                            (
                                <div className="row">
                                    <div className="col-xs-12">
                                        <div style={{ width: '100%', margin: '20px 0' }}>
                                            <p className="text-muted" style={{fontSize: 18, textAlign: 'center'}}>
                                                No hay noticias disponibles en este momento...
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )
                    }
                </div>
            </div>
        </React.Fragment>
    );
};